import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, tap } from 'rxjs';
import {
    IAppMenu,
    IAppSubMenu,
    INavMenu,
} from '../../../../interfaces/IAppRoutes';
import { LayoutService } from './../../services/layout.service';
import { NzIconService } from 'ng-zorro-antd/icon';
import { getNzIcon } from '../../../icon';
import { IconType } from '@pf/shared-common';

@UntilDestroy()
@Component({
    selector: 'pf-app-nav-menu',
    templateUrl: './app-nav-menu.component.html',
    styleUrls: ['./app-nav-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppNavMenuComponent {
    layoutService = inject(LayoutService);
    private iconService = inject(NzIconService);

    @Input() navigation: IAppMenu[] = [];

    constructor() {
        const layoutService = this.layoutService;
        const cdr = inject(ChangeDetectorRef);

        layoutService.isCollapsed$
            .pipe(
                untilDestroyed(this),
                distinctUntilChanged(),
                tap(() => cdr.markForCheck())
            )
            .subscribe();
    }

    navigationTrackByFn(_: number, menu: INavMenu) {
        return menu.displayName;
    }

    shouldHideFn(menu: IAppMenu) {
        if (
            !menu ||
            menu.hide ||
            !menu.subMenu ||
            menu.subMenu.filter(m => !m.hide).length === 0
        ) {
            return true;
        }

        return;
    }

    getNzIcon(icon: keyof typeof IconType | undefined) {
        return getNzIcon(icon);
    }

    get showMenuOnOpen(): boolean {
        return window.innerWidth < 992 ? false : true;
    }

    hasGroups(menu: IAppMenu): boolean {
        return menu.subMenu?.some(item => !!item.group) ?? false;
    }

    getGroups(menu: IAppMenu): string[] {
        const groups = new Set<string>();
        menu.subMenu
            ?.filter(item => !!item.group)
            .forEach(item => groups.add(item.group!));
        return Array.from(groups);
    }

    getItemsForGroup(menu: IAppMenu, group: string): IAppSubMenu[] {
        return menu.subMenu?.filter(item => item.group === group) ?? [];
    }
}
